import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, Upload, FileText, Loader2, ArrowRight, FileSearch, ScanSearch, TrendingUp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import {
  GradeHeroCard, ScoreCard, FullReport,
  IntelAnalysisBox, DownloadButton,
} from '../components/LOIVerificationComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const UPLOAD_STAGES = [
  { label: 'UPLOADING', duration: 2000 },
  { label: 'LOADING', duration: 2000 },
  { label: 'ANALYSING', duration: 2000 },
  { label: 'REPORTING', duration: 2000 },
  { label: 'SAVING FILE', duration: 2000 },
  { label: 'FINISHING', duration: 500 },
];

const VerifyLOIPage = () => {
  const { user } = useAuth();
  const { userTheme } = useTheme();
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [uploadStage, setUploadStage] = useState(null);

  const ROTATING_WORDS = ['Protection.', 'Confidence.', 'Transparency.', 'Trust.'];
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const pageStyles = userTheme?.page || {};
  const buttonStyles = userTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';

  const FEATURE_CARDS = [
    {
      icon: FileSearch,
      title: '40+ Checks',
      description: 'Content fields, metadata, edit history, stamps, and forensic analysis',
      color: '#3B82F6',
      bg: '#EFF6FF',
    },
    {
      icon: ScanSearch,
      title: 'Fraud Detection',
      description: 'Identifies signs of likely manipulation, altered stamps, and modified documents',
      color: '#22C55E',
      bg: '#F0FDF4',
    },
    {
      icon: TrendingUp,
      title: 'Instant Grade',
      description: 'A-E grading system tells you immediately if the LOI is legitimate',
      color: '#6366F1',
      bg: '#EEF2FF',
    },
  ];

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.type === 'application/pdf') {
      setFile(dropped);
      setError('');
    } else {
      setError('Please upload a PDF file');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      setError('');
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setResult(null);
    setUploadStage({ index: 0, label: UPLOAD_STAGES[0].label, progress: 0 });

    const startTime = Date.now();
    let apiResult = null;
    let apiError = null;

    // Start the API call immediately
    const apiPromise = (async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/loi-verification/analyze', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });

        apiResult = response.data;
      } catch (err) {
        apiError = err;
      }
    })();

    // Animate through stages with minimum total of 10 seconds
    const totalMinDuration = 10000;
    const stageCount = UPLOAD_STAGES.length;
    const perStageDuration = totalMinDuration / stageCount;

    for (let i = 0; i < stageCount; i++) {
      setUploadStage({ index: i, label: UPLOAD_STAGES[i].label, progress: Math.round(((i + 0.5) / stageCount) * 100) });
      if (i === stageCount - 1) {
        await apiPromise;
        setUploadStage({ index: i, label: UPLOAD_STAGES[i].label, progress: 100 });
        await new Promise(r => setTimeout(r, 500));
      } else {
        await new Promise(r => setTimeout(r, perStageDuration));
      }
    }

    const elapsed = Date.now() - startTime;
    if (elapsed < totalMinDuration) {
      await new Promise(r => setTimeout(r, totalMinDuration - elapsed));
    }

    setUploadStage(null);
    setUploading(false);

    if (apiError) {
      const detail = apiError.response?.data?.detail || apiError.message || 'Failed to analyze document';
      setError(detail);
    } else if (apiResult) {
      setResult(apiResult);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError('');
    setUploadStage(null);
  };

  const handleDownloadReport = async () => {
    if (!result?.verification_id) return;
    try {
      const response = await api.get(`/loi-verification/report/${result.verification_id}`, {
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      let filename = 'Trados_Report.pdf';
      if (disposition) {
        const match = disposition.match(/filename\*=UTF-8''(.+)/);
        if (match) filename = decodeURIComponent(match[1]);
        else {
          const fallback = disposition.match(/filename="?([^";\n]+)"?/);
          if (fallback) filename = fallback[1];
        }
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download report. Please try again.');
    }
  };

  const isPaidUser = user?.has_active_membership;

  return (
    <Sidebar>
      <div
        className="p-6 md:p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#f9fafb' }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Header — hidden when results are showing */}
          {!result && (
            <div className="mb-8 text-center">
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">
                Verify Your LOI{' '}
                <span className="bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  In Seconds
                </span>
              </h1>
              <p className="text-gray-500 mt-3 text-base max-w-lg mx-auto">
                Upload any Letter of Intent and our AI-powered engine
                will analyze it for completeness, authenticity, and red flags.
              </p>
            </div>
          )}

          {/* Upload Zone (shown when no result yet) */}
          {!result && (
            <div className="mb-8">
              <style>{`
                @property --border-angle {
                  syntax: '<angle>';
                  initial-value: 0deg;
                  inherits: false;
                }
                @keyframes spin-border {
                  to { --border-angle: 360deg; }
                }
              `}</style>
              <div
                className="max-w-xl mx-auto rounded-2xl p-[3px] relative overflow-hidden"
                style={{
                  background: dragging
                    ? '#60a5fa'
                    : file
                    ? '#4ade80'
                    : 'conic-gradient(from var(--border-angle, 0deg), #F58A5A, #FFD166, #F07A4A, #FFCE47, #E8734A, #FFD166, #F58A5A)',
                  animation: (!dragging && !file) ? 'spin-border 3s linear infinite' : 'none',
                }}
              >
                <div
                  className={`rounded-[13px] p-12 transition ${
                    uploading ? 'bg-gray-50' : dragging ? 'bg-blue-50 cursor-pointer' : file ? 'bg-green-50 cursor-pointer' : 'bg-white cursor-pointer'
                  }`}
                  onDrop={!uploading ? handleDrop : undefined}
                  onDragOver={!uploading ? (e) => { e.preventDefault(); setDragging(true); } : undefined}
                  onDragLeave={!uploading ? () => setDragging(false) : undefined}
                  onClick={!uploading ? () => document.getElementById('pdf-upload-internal').click() : undefined}
                >
                  <input
                    id="pdf-upload-internal"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {uploading && uploadStage ? (
                    <div className="text-center">
                      <Loader2 className="w-10 h-10 text-blue-500 mx-auto mb-4 animate-spin" />
                      <motion.p
                        key={uploadStage.label}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-lg font-bold text-gray-800 tracking-wider mb-3"
                      >
                        {uploadStage.label}
                      </motion.p>
                      <div className="w-full max-w-xs mx-auto">
                        <div className="w-full h-3 rounded-full bg-gray-200 overflow-hidden">
                          <motion.div
                            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadStage.progress}%` }}
                            transition={{ duration: 0.8, ease: 'easeInOut' }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-2">{uploadStage.progress}% complete</p>
                      </div>
                      <p className="text-sm text-gray-500 mt-3">{file?.name}</p>
                    </div>
                  ) : file ? (
                    <div className="text-center">
                      <FileText className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <p className="text-gray-900 font-medium">{file.name}</p>
                      <p className="text-gray-400 text-sm mt-1">{(file.size / 1024).toFixed(0)} KB</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">Drop your PDF here or click to browse</p>
                      <p className="text-gray-400 text-sm mt-1">Maximum 20MB</p>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <p className="text-red-500 text-sm mt-4 text-center">{error}</p>
              )}

              {file && !uploading && (
                <div className="mt-6 flex gap-3 justify-center">
                  <button
                    onClick={handleReset}
                    className="px-6 py-3 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 text-sm"
                  >
                    Change File
                  </button>
                  <button
                    onClick={handleAnalyze}
                    disabled={uploading}
                    className="px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 font-medium text-sm disabled:opacity-50 flex items-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Analyze LOI
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Features Section (shown when no result) */}
          {!result && !uploading && (
            <div className="mt-12 mb-8">
              {/* Rotating tagline */}
              <h2 className="text-2xl md:text-3xl font-extrabold text-center text-gray-900 mb-10">
                Trados will bring you{' '}
                <AnimatePresence mode="wait">
                  <motion.span
                    key={ROTATING_WORDS[wordIndex]}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.35 }}
                    className="bg-gradient-to-r from-orange-400 to-yellow-500 bg-clip-text text-transparent inline-block"
                  >
                    {ROTATING_WORDS[wordIndex]}
                  </motion.span>
                </AnimatePresence>
              </h2>

              {/* Feature cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto">
                {FEATURE_CARDS.map((card) => (
                  <div
                    key={card.title}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: card.bg }}
                    >
                      <card.icon className="w-6 h-6" style={{ color: card.color }} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{card.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{card.description}</p>
                  </div>
                ))}
              </div>

              {/* CTA button */}
              {!file && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={() => document.getElementById('pdf-upload-internal').click()}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full hover:from-blue-600 hover:to-cyan-600 font-medium text-sm flex items-center gap-2 shadow-lg shadow-blue-500/25"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Free LOI Compliance Check
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {result && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={handleReset}
                  className="text-gray-400 hover:text-gray-700 text-sm flex items-center gap-1"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" /> Analyze another LOI
                </button>
                <DownloadButton onClick={handleDownloadReport} />
              </div>

              {/* User paid status indicator */}
              {result.is_paid_user !== undefined && (
                <div className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium text-center ${
                  result.is_paid_user
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`}>
                  {result.is_paid_user
                    ? 'Paid Member — Full Intel Access'
                    : 'Free User — Intel preview only (first 30 characters)'}
                </div>
              )}

              <GradeHeroCard result={result} />

              <div className="grid grid-cols-3 gap-4 mt-6">
                <ScoreCard label="Level 1 — Must Have" value={result.grading?.level_1} color="red" />
                <ScoreCard label="Level 2 — Important" value={result.grading?.level_2} color="orange" />
                <ScoreCard label="Level 3 — Nice to Have" value={result.grading?.level_3} color="blue" />
              </div>

              <FullReport result={result} />


              <div className="mt-6 flex flex-col items-center gap-2">
                <DownloadButton onClick={handleDownloadReport} large />
                {result?.verification_id && (
                  <p className="text-xs text-gray-400 break-all text-center">
                    Report ID: {result.verification_id}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default VerifyLOIPage;
